
import { useEffect, useMemo, useState, useCallback } from "react";
import "./biolink.css";
import API from "../../services/api.js";

// ==========================================================
// DEBUTS CROP — BIOLINK
// React + Django REST Framework
// Desarrollo y producción
// ==========================================================

// Desarrollo:
// VITE_API_URL=http://localhost:8000/api
// VITE_BACKEND_URL=http://localhost:8000
//
// Producción:
// VITE_API_URL=https://api.debutscrop.com/api
// VITE_BACKEND_URL=https://api.debutscrop.com

const API_BASE_URL = (
  import.meta.env.VITE_API_URL || "/api"
).replace(/\/+$/, "");

const BACKEND_URL = (
  import.meta.env.VITE_BACKEND_URL ||
  API_BASE_URL.replace(/\/api\/?$/, "")
).replace(/\/+$/, "");
const FALLBACK_PROFILE = {
  name: "La Esquina del Parqués",
  username: "@laesquinadelparques",
  description:
    "Licorera · Granizados · Música · Torneos de Parqués",
  tagline: "El lugar donde la noche se juega diferente",
  logo: "/images/logo.png",
  background_image: "/images/bg.png",
  verified: true,

  // Configura el número real de WhatsApp del bar.
  whatsapp_number: "",
  whatsapp_message:
    "Hola, La Esquina del Parqués. Quiero conocer el menú y las promociones.",

  active: true,
  views: 0,
  clicks: 0,

  items: [
    {
      id: "fallback-menu",
      title: "NUESTRO MENÚ",
      subtitle: "Bebidas, granizados y sabores",
      icon: "🍹",
      url: "/menu",
      active: true,
      order: 1,
    },
    {
      id: "fallback-licorera",
      title: "LICORERA",
      subtitle: "Descubre nuestras bebidas",
      icon: "🍾",
      url: "/catalog",
      active: true,
      order: 2,
    },
    {
      id: "fallback-tournaments",
      title: "TORNEOS DE PARQUÉS",
      subtitle: "Ven a jugar y vivir la experiencia",
      icon: "🎲",
      url: "/torneos",
      active: true,
      order: 3,
    },
    {
      id: "fallback-whatsapp",
      title: "RESERVAS Y EVENTOS",
      subtitle: "Consulta disponibilidad",
      icon: "💬",
      url: "#whatsapp",
      active: true,
      order: 4,
    },
    {
      id: "fallback-instagram",
      title: "INSTAGRAM",
      subtitle: "Síguenos y descubre el ambiente",
      icon: "◎",
      url: "https://www.instagram.com/laesquinadelparques_oficial/",
      active: true,
      order: 5,
    },
    {
      id: "fallback-location",
      title: "CÓMO LLEGAR",
      subtitle: "Encuentra nuestro bar",
      icon: "⌖",
      url: "/sedes",
      active: true,
      order: 6,
    },
  ],

  products: [],
};
// ==========================================================
// UTILIDADES
// ==========================================================

function getMediaUrl(url) {
  if (!url || typeof url !== "string") {
    return "";
  }

  const value = url.trim();

  if (
    value.startsWith("https://") ||
    value.startsWith("http://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  // Los archivos cargados a Django suelen estar en /media/.
  if (
    value.startsWith("/media/") ||
    value.startsWith("media/")
  ) {
    return `${BACKEND_URL}/${value.replace(/^\/+/, "")}`;
  }

  // Rutas relativas de archivos multimedia sin slash inicial.
  if (value.startsWith("media/")) {
    return `${BACKEND_URL}/${value}`;
  }

  // Imágenes públicas del frontend, por ejemplo /images/logo.png.
  return value;
}

function unwrapProfile(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  // Respuesta normal de un serializer DRF.
  if (Array.isArray(payload.results)) {
    return payload.results[0] || null;
  }

  // Algunas APIs envuelven los datos en "data".
  if (
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }

  return payload;
}

function isNumericId(id) {
  return (
    (typeof id === "number" && Number.isFinite(id)) ||
    (typeof id === "string" && /^\d+$/.test(id))
  );
}

function formatPrice(value) {
  if (value === null || value === undefined || value === "") {
    return "Consultar";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return String(value);
  }

  return number.toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  });
}

function normalizeInternalUrl(url) {
  const value = String(url || "").trim();

  if (!value) {
    return "/";
  }

  if (
    value.startsWith("https://") ||
    value.startsWith("http://") ||
    value.startsWith("mailto:") ||
    value.startsWith("tel:")
  ) {
    return value;
  }

  return value.startsWith("/") ? value : `/${value}`;
}

// ==========================================================
// COMPONENTE
// ==========================================================

export default function BioLink() {
  const [data, setData] = useState(FALLBACK_PROFILE);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState(false);
  const [openingProduct, setOpeningProduct] = useState(null);

  // --------------------------------------------------------
  // CARGAR PERFIL DESDE DJANGO
  // GET /api/biolink/
  // --------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      try {
        const response = await API.get("/biolink/");
        const backendData = unwrapProfile(response?.data);

        if (!backendData || typeof backendData !== "object") {
          throw new Error(
            "Django no devolvió un perfil válido de BioLink."
          );
        }

        if (backendData.active === false) {
          throw new Error("El perfil BioLink está desactivado.");
        }

        if (!mounted) {
          return;
        }

        setData({
          ...FALLBACK_PROFILE,
          ...backendData,
          items: Array.isArray(backendData.items)
            ? backendData.items
            : [],
          products: Array.isArray(backendData.products)
            ? backendData.products
            : [],
        });

        setBackendError(false);
      } catch (error) {
        console.error(
          "[BioLink] No se pudo cargar el perfil desde Django:",
          error
        );

        if (mounted) {
          setData(FALLBACK_PROFILE);
          setBackendError(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      mounted = false;
    };
  }, []);

  // --------------------------------------------------------
  // WHATSAPP
  // --------------------------------------------------------

  const whatsapp = useMemo(() => {
    const phone = String(
      data.whatsapp_number ||
        FALLBACK_PROFILE.whatsapp_number ||
        ""
    ).replace(/\D/g, "");

    const message = encodeURIComponent(
      data.whatsapp_message ||
        FALLBACK_PROFILE.whatsapp_message
    );

    return phone
      ? `https://wa.me/${phone}?text=${message}`
      : `https://wa.me/?text=${message}`;
  }, [data.whatsapp_number, data.whatsapp_message]);

  // --------------------------------------------------------
  // REGISTRAR CLIC EN ENLACE
  // POST /api/biolink/items/{id}/click/
  // --------------------------------------------------------

  const registerItemClick = useCallback(async (item) => {
    if (!isNumericId(item?.id)) {
      return;
    }

    try {
      await API.post(`/biolink/items/${item.id}/click/`);
    } catch (error) {
      console.error(
        "[BioLink] Error registrando clic de enlace:",
        error
      );
    }
  }, []);

  // --------------------------------------------------------
  // REGISTRAR VISTA DE PRODUCTO
  // POST /api/biolink/products/{id}/view/
  // --------------------------------------------------------

  const registerProductView = useCallback(async (product) => {
    if (!isNumericId(product?.id)) {
      return;
    }

    try {
      await API.post(`/biolink/products/${product.id}/view/`);
    } catch (error) {
      console.error(
        "[BioLink] Error registrando vista de producto:",
        error
      );
    }
  }, []);

  // --------------------------------------------------------
  // REGISTRAR CLIC DE PRODUCTO
  // POST /api/biolink/products/{id}/click/
  // --------------------------------------------------------

  const registerProductClick = useCallback(async (product) => {
    if (!isNumericId(product?.id)) {
      return;
    }

    try {
      await API.post(`/biolink/products/${product.id}/click/`);
    } catch (error) {
      console.error(
        "[BioLink] Error registrando clic de producto:",
        error
      );
    }
  }, []);

  // --------------------------------------------------------
  // ABRIR ENLACE
  // --------------------------------------------------------

  const openLink = useCallback(async (item) => {
    if (!item?.url) {
      return;
    }

    // Registrar el clic sin bloquear la navegación.
    void registerItemClick(item);

    const url = String(item.url).trim();

    if (url === "#whatsapp") {
      window.open(whatsapp, "_blank", "noopener,noreferrer");
      return;
    }

    if (/^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    if (/^(mailto:|tel:)/i.test(url)) {
      window.location.href = url;
      return;
    }

    window.location.assign(normalizeInternalUrl(url));
  }, [registerItemClick, whatsapp]);

  // --------------------------------------------------------
  // ABRIR PRODUCTO
  // --------------------------------------------------------

  const openProduct = useCallback(async (product) => {
    if (!product || openingProduct !== null) {
      return;
    }

    if (
      product.is_in_stock === false ||
      (
        product.stock !== null &&
        product.stock !== undefined &&
        Number(product.stock) <= 0
      )
    ) {
      return;
    }

    setOpeningProduct(product.id);

    try {
      // Se intenta registrar ambas métricas.
      await Promise.allSettled([
        registerProductView(product),
        registerProductClick(product),
      ]);

      const productUrl =
        product.url ||
        (
          product.product_id
            ? `/catalog/product/${product.slug || product.product_id}`
            : "/catalog"
        );

      if (/^https?:\/\//i.test(productUrl)) {
        window.location.assign(productUrl);
        return;
      }

      window.location.assign(normalizeInternalUrl(productUrl));
    } catch (error) {
      console.error(
        "[BioLink] Error abriendo producto:",
        error
      );

      setOpeningProduct(null);
    }
  }, [
    openingProduct,
    registerProductView,
    registerProductClick,
  ]);

  // --------------------------------------------------------
  // ENLACES ACTIVOS Y ORDENADOS
  // --------------------------------------------------------

  const items = useMemo(() => {
    return [...(data.items || [])]
      .filter((item) => item && item.active !== false)
      .sort(
        (a, b) =>
          Number(a.order || 0) - Number(b.order || 0)
      );
  }, [data.items]);

  // --------------------------------------------------------
  // PRODUCTOS ACTIVOS Y DISPONIBLES
  // --------------------------------------------------------

  const products = useMemo(() => {
    return [...(data.products || [])]
      .filter((product) => {
        if (!product || product.active === false) {
          return false;
        }

        if (product.is_in_stock === false) {
          return false;
        }

        if (
          product.stock !== null &&
          product.stock !== undefined &&
          Number(product.stock) <= 0
        ) {
          return false;
        }

        return true;
      })
      .sort(
        (a, b) =>
          Number(a.order || 0) - Number(b.order || 0)
      );
  }, [data.products]);

  // --------------------------------------------------------
  // REDES SOCIALES
  // --------------------------------------------------------

  const socialItems = useMemo(() => {
    return items.filter((item) => {
      const url = String(item?.url || "").toLowerCase();

      return (
        url.includes("instagram.com") ||
        url.includes("facebook.com") ||
        url.includes("tiktok.com")
      );
    });
  }, [items]);

  // --------------------------------------------------------
  // ESTADO DE CARGA
  // --------------------------------------------------------

  if (loading) {
    return (
      <main
        className="bio-page"
        style={{
          "--bio-bg": `url("${getMediaUrl(
            FALLBACK_PROFILE.background_image
          )}")`,
        }}
      >
        <div className="bio-overlay" />

        <section className="bio-shell bio-loading">
          <img
            className="bio-logo"
            src={getMediaUrl(FALLBACK_PROFILE.logo)}
            alt="Debuts Crop"
          />

          <p>Cargando Debuts Crop...</p>
        </section>
      </main>
    );
  }

  // --------------------------------------------------------
  // RENDER PRINCIPAL
  // --------------------------------------------------------

  return (
    <main
      className="bio-page"
      style={{
        "--bio-bg": `url("${getMediaUrl(
          data.background_image ||
            FALLBACK_PROFILE.background_image
        )}")`,
      }}
    >
      <div className="bio-overlay" />

      <section className="bio-shell">
        {/* HEADER */}

        <header className="bio-header">
          <div className="bio-top-art">
            <img
              className="bio-logo"
              src={getMediaUrl(
                data.logo || FALLBACK_PROFILE.logo
              )}
              alt={data.name || "Debuts Crop"}
            />
          </div>

          <div className="bio-avatar">
            <img
              src={getMediaUrl(
                data.logo || FALLBACK_PROFILE.logo
              )}
              alt={data.name || "Debuts Crop"}
            />
          </div>

          <h1>
            {data.username || "@debutscrop"}

            {data.verified && (
              <span
                className="verified"
                title="Cuenta verificada"
              >
                ✓
              </span>
            )}
          </h1>

          {data.description && (
            <p className="bio-description">
              {data.description}
            </p>
          )}

          {data.tagline && <small>{data.tagline}</small>}

          <div className="bio-divider">
            <span />
            <b>♛</b>
            <span />
          </div>
        </header>

        {/* AVISO DE CONEXIÓN */}

        {backendError && (
          <p role="status" className="bio-connection-warning">
            No se pudo conectar con el servidor. Se muestran
            temporalmente los datos de respaldo.
          </p>
        )}

        {/* ENLACES */}

        {items.length > 0 && (
          <section className="bio-links">
            {items.map((item) => (
              <button
                key={item.id ?? item.title}
                className="bio-link-card"
                type="button"
                onClick={() => openLink(item)}
              >
                <span className="bio-link-icon">
                  {item.icon || "↗"}
                </span>

                <span className="bio-link-copy">
                  <strong>{item.title}</strong>

                  {item.subtitle && (
                    <small>{item.subtitle}</small>
                  )}
                </span>

                <span className="bio-arrow">→</span>
              </button>
            ))}
          </section>
        )}

        {/* PRODUCTOS DESTACADOS */}

        {products.length > 0 && (
          <section className="bio-products">
            <div className="bio-section-title">
              <span />
              <h2>PRODUCTOS DESTACADOS</h2>
              <span />
            </div>

            <div className="bio-product-row">
              {products.map((product, index) => {
                const image = getMediaUrl(product.image);
                const isOpening = openingProduct === product.id;

                return (
                  <article
                    className="bio-product"
                    key={product.id ?? index}
                    role="button"
                    tabIndex={0}
                    aria-busy={isOpening}
                    onClick={() => {
                      if (!isOpening) {
                        void openProduct(product);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === " "
                      ) {
                        event.preventDefault();

                        if (!isOpening) {
                          void openProduct(product);
                        }
                      }
                    }}
                  >
                    <div className="bio-product-image">
                      {image ? (
                        <img
                          src={image}
                          alt={product.name || "Producto Debuts Crop"}
                          loading="lazy"
                        />
                      ) : (
                        <span>
                          DEBUTS
                          <br />
                          CROP
                        </span>
                      )}

                      {product.has_discount && (
                        <em>
                          -{product.discount_percentage}%
                        </em>
                      )}
                    </div>

                    <div className="bio-product-name">
                      {product.name || "Producto"}
                    </div>

                    <div className="bio-product-bottom">
                      <strong>{formatPrice(product.price)}</strong>
                      <span>{isOpening ? "..." : "🛒"}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* WHATSAPP */}

        <a
          className="bio-whatsapp"
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="bio-whatsapp-icon">◔</span>

          <span>
            <small>¿TIENES DUDAS?</small>
            <strong>CHATEA CON NOSOTROS</strong>
          </span>

          <b>→</b>
        </a>

        {/* REDES SOCIALES Y PIE */}

        <footer className="bio-footer">
          {socialItems.length > 0 && (
            <div className="bio-socials">
              {socialItems.map((item) => {
                const url = String(item.url || "").toLowerCase();

                let symbol = "↗";

                if (url.includes("instagram.com")) {
                  symbol = "◎";
                } else if (url.includes("tiktok.com")) {
                  symbol = "♪";
                } else if (url.includes("facebook.com")) {
                  symbol = "f";
                }

                return (
                  <button
                    key={item.id ?? item.title}
                    type="button"
                    onClick={() => openLink(item)}
                    aria-label={item.title || "Red social"}
                  >
                    {symbol}
                  </button>
                );
              })}

              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                ◔
              </a>

              <button
                type="button"
                onClick={() => {
                  window.location.assign("/sedes");
                }}
                aria-label="Locales"
              >
                ⌖
              </button>
            </div>
          )}

          <div className="bio-footer-line">
            <span />
            <small>DEBUTS CROP</small>
            <span />
          </div>

          <div className="bio-footer-copy">
            © {new Date().getFullYear()} Debuts Crop
          </div>
        </footer>
      </section>
    </main>
  );
}
